import { useCallback, useState } from 'react';

import { useInterval } from '../useInterval';

export const ONE_SECOND_MS = 1_000;
export const MINUTE_MS = 60 * ONE_SECOND_MS;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

export interface UseCountdownProps {
  endTime: number;
  startTime?: number;
  onTimeout?: () => void;
  intervalMs?: number;
}

export const timeDifferenceToDHMS = (timeDifference: number) => {
  if (timeDifference > 0) {
    return {
      days: Math.floor(timeDifference / DAY_MS),
      hours: Math.floor(timeDifference / HOUR_MS) % 24,
      minutes: Math.floor((timeDifference / MINUTE_MS) % 60),
      seconds: Math.floor((timeDifference / ONE_SECOND_MS) % 60),
    };
  }

  return {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
};

export type TimeLeft = ReturnType<typeof timeDifferenceToDHMS>;

/**
 * Calculates time left from endTime to now (start time)
 */
export const useCountdown = ({
  endTime,
  startTime = Date.now(),
  onTimeout,
  intervalMs = ONE_SECOND_MS,
}: UseCountdownProps): TimeLeft => {
  const [isFinished, setFinished] = useState(false);

  const initialTimeDifference = endTime - startTime;
  const [timeLeft, setTimeLeft] = useState(initialTimeDifference);

  const decrementTime = useCallback(() => setTimeLeft((prev) => prev - intervalMs), [intervalMs]);

  const countdown = useCallback(() => {
    if (timeLeft <= 0) {
      onTimeout?.();
      setFinished(true);
      return;
    }

    decrementTime();
  }, [timeLeft, decrementTime, onTimeout]);

  useInterval(countdown, !isFinished ? intervalMs : null, { leading: false });

  return timeDifferenceToDHMS(timeLeft);
};
